import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export type CustomItemType = "Artist" | "Album" | "Song";

// The image is optional, but keep it small enough that a user's whole custom
// library can be sent back down in a single request.
const MAX_IMAGE_LENGTH = 150000;
const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

export const listTypeToCustomType = (listType: string): CustomItemType => {
  if (listType === "Artists") return "Artist";
  if (listType === "Songs") return "Song";
  return "Album";
};

type CustomItemPopupProps = {
  open: boolean;
  onClose: () => void;
  defaultType: CustomItemType;
  defaultName?: string;
  basicFunctions: any;
  createCommands: any;
  postCommands: any;
  mainFunctions: any;
  state: any;
};

const emptyForm = {
  name: "",
  artistName: "",
  albumName: "",
  yearReleased: "",
};

const CustomItemPopup = (props: CustomItemPopupProps) => {
  const { open, onClose, defaultType, defaultName, state } = props;
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [itemType, setItemType] = useState<CustomItemType>(defaultType);
  const [form, setForm] = useState({ ...emptyForm });
  const [image, setImage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [processingImage, setProcessingImage] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setItemType(defaultType);
    setForm({ ...emptyForm, name: defaultName || "" });
    setImage(null);
    setSubmitting(false);
  }, [open, defaultType, defaultName]);

  const existingItems = useMemo(() => {
    const customItems = state?.customItems || {};
    return customItems[`${itemType}s`] || [];
  }, [state?.customItems, itemType]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleImagePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error(t("customItems.invalidImage"));
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(t("customItems.imageTooLarge"));
      return;
    }

    setProcessingImage(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error("read failed"));
        reader.readAsDataURL(file);
      });

      // Shrink until it comfortably fits the column, so covers stay cheap to send.
      let resized = await props.basicFunctions.generateThumbnail(dataUrl, 300, 0.7);
      if (resized.length > MAX_IMAGE_LENGTH) {
        resized = await props.basicFunctions.generateThumbnail(dataUrl, 220, 0.5);
      }
      if (resized.length > MAX_IMAGE_LENGTH) {
        toast.error(t("customItems.imageTooLarge"));
        return;
      }
      setImage(resized);
    } catch {
      toast.error(t("customItems.invalidImage"));
    } finally {
      setProcessingImage(false);
    }
  };

  const handleSubmit = async () => {
    const trimmedName = form.name.trim();
    if (!trimmedName) return;

    setSubmitting(true);
    const result = await props.createCommands.createCustomItem({
      itemType,
      name: trimmedName,
      artistName: itemType === "Artist" ? "" : form.artistName.trim(),
      albumName: itemType === "Song" ? form.albumName.trim() : "",
      yearReleased: itemType === "Artist" ? null : form.yearReleased.trim() || null,
      imageData: image,
    });
    setSubmitting(false);

    if (!result?.success) {
      toast.error(result?.error || t("customItems.addFailed"));
      return;
    }

    await props.mainFunctions.loadCustomItems();
    toast.success(t("customItems.added"));
    onClose();
  };

  const handleDelete = async (customItemId: number) => {
    setDeletingId(customItemId);
    const result = await props.postCommands.deleteCustomItem(customItemId);
    setDeletingId(null);
    if (result?.success) {
      await props.mainFunctions.loadCustomItems();
      toast.success(t("customItems.deleted"));
    } else {
      toast.error(t("customItems.deleteFailed"));
    }
  };

  const nameLabel =
    itemType === "Artist"
      ? t("customItems.artistName")
      : itemType === "Album"
        ? t("customItems.albumName")
        : t("customItems.songName");

  const itemLabel = (item: any) =>
    [item.Artist, item.AlbumName, item.SongName].filter(Boolean).join(" — ") || item.Artist;

  return (
    <Dialog id="custom-item-popup" open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("customItems.title")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {t("customItems.description")}
        </Typography>

        <FormControl fullWidth margin="dense">
          <InputLabel id="custom-item-type-label">{t("customItems.type")}</InputLabel>
          <Select
            labelId="custom-item-type-label"
            value={itemType}
            label={t("customItems.type")}
            onChange={(e) => setItemType(e.target.value as CustomItemType)}
          >
            <MenuItem value="Artist">{t("customItems.typeArtist")}</MenuItem>
            <MenuItem value="Album">{t("customItems.typeAlbum")}</MenuItem>
            <MenuItem value="Song">{t("customItems.typeSong")}</MenuItem>
          </Select>
        </FormControl>

        <TextField
          autoFocus
          margin="dense"
          label={nameLabel}
          fullWidth
          variant="outlined"
          value={form.name}
          onChange={handleChange("name")}
          inputProps={{ maxLength: 255 }}
        />

        {itemType !== "Artist" && (
          <TextField
            margin="dense"
            label={t("customItems.artistName")}
            fullWidth
            variant="outlined"
            value={form.artistName}
            onChange={handleChange("artistName")}
            inputProps={{ maxLength: 255 }}
          />
        )}

        {itemType === "Song" && (
          <TextField
            margin="dense"
            label={t("customItems.albumName")}
            fullWidth
            variant="outlined"
            value={form.albumName}
            onChange={handleChange("albumName")}
            inputProps={{ maxLength: 255 }}
          />
        )}

        {itemType !== "Artist" && (
          <TextField
            margin="dense"
            label={t("customItems.year")}
            fullWidth
            variant="outlined"
            type="number"
            value={form.yearReleased}
            onChange={handleChange("yearReleased")}
          />
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
          <Box
            sx={{
              width: 90,
              height: 90,
              flexShrink: 0,
              borderRadius: "6px",
              border: "1px solid var(--border-mid)",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "var(--hover-overlay)",
            }}
          >
            <img
              src={image || "/Images/DefaultAlbumCover.svg"}
              alt={t("customItems.imagePreview")}
              width={90}
              height={90}
              style={{ objectFit: "cover" }}
            />
          </Box>
          <Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={processingImage ? <CircularProgress size={16} /> : <AddPhotoAlternateIcon />}
              disabled={processingImage}
              onClick={() => fileInputRef.current?.click()}
              sx={{ textTransform: "none" }}
            >
              {image ? t("customItems.changeImage") : t("customItems.uploadImage")}
            </Button>
            {image && (
              <Button
                size="small"
                startIcon={<CloseIcon />}
                onClick={() => setImage(null)}
                sx={{ textTransform: "none", ml: 1 }}
              >
                {t("customItems.removeImage")}
              </Button>
            )}
            <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 0.5 }}>
              {t("customItems.imageOptional")}
            </Typography>
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleImagePick}
          />
        </Box>

        {existingItems.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("customItems.yourItems", { count: existingItems.length })}
            </Typography>
            <Box
              sx={{
                maxHeight: 180,
                overflowY: "auto",
                border: "1px solid var(--border-soft)",
                borderRadius: "6px",
              }}
            >
              {existingItems.map((item: any) => (
                <Box
                  key={item.Id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1,
                    py: 0.5,
                    borderBottom: "1px solid var(--border-soft)",
                    "&:last-of-type": { borderBottom: "none" },
                  }}
                >
                  <img
                    src={item.CustomImage || "/Images/DefaultAlbumCover.svg"}
                    alt=""
                    width={32}
                    height={32}
                    style={{ objectFit: "cover", borderRadius: "4px" }}
                  />
                  <Typography variant="body2" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {itemLabel(item)}
                  </Typography>
                  <Tooltip title={t("customItems.delete")}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={deletingId === item.CustomItemId}
                        onClick={() => handleDelete(item.CustomItemId)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t("customItems.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!form.name.trim() || submitting || processingImage}
        >
          {t("customItems.add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomItemPopup;
